import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { IonButton, IonContent, IonHeader, IonModal, IonTitle, IonToolbar, IonButtons, IonIcon, IonText, IonSpinner, IonToast } from '@ionic/react';
import { close, cameraReverse } from 'ionicons/icons';
import './QrScanner.css';

interface QrScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (decodedText: string) => void;
  userId?: string;
  userCed?: string;
  eventCode?: string;
  sendAttendance?: boolean;
  title?: string;
}

const QrScanner: React.FC<QrScannerProps> = ({
  isOpen,
  onClose,
  onScanSuccess,
  sendAttendance = false,
  title = "Escáner QR"
}) => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');
  const [availableCameras, setAvailableCameras] = useState<any[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const isMountedRef = useRef(true);
  const scannerElementId = 'qr-scanner-element';

  // Inicializar escáner cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      setScanning(false);
      console.log('🎥 Modal opened, initializing Html5Qrcode scanner...');

      // Usar un timeout más corto ya que el elemento siempre está presente
      const timeoutId = setTimeout(() => {
        initializeScanner();
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      console.log('🔒 Modal closed, cleaning up...');
      stopScanner();
    }
  }, [isOpen]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      console.log('Component unmounting, cleaning up...');
      isMountedRef.current = false;
      stopScanner();
    };
  }, []);

  // Función para inicializar el escáner Html5Qrcode
  const initializeScanner = async () => {
    console.log('🚀 QrScanner: initializeScanner called, isMounted:', isMountedRef.current);

    if (!isMountedRef.current) {
      console.log('❌ QrScanner: Component not mounted, aborting initialization');
      return;
    }

    try {
      setError(null);
      setLoading(true);
      console.log('🚀 QrScanner: Starting Html5Qrcode initialization...');

      // Verificar si hay cámaras disponibles
      console.log('📷 QrScanner: Getting cameras...');
      const devices = await Html5Qrcode.getCameras();
      console.log('📷 QrScanner: Found cameras:', devices?.length || 0);

      if (!devices || devices.length === 0) {
        throw new Error('No se encontraron cámaras disponibles');
      }

      // Guardar las cámaras disponibles
      setAvailableCameras(devices);

      // Verificar que el elemento DOM existe
      const scannerElement = document.getElementById(scannerElementId);
      if (!scannerElement) {
        throw new Error(`Elemento del escáner no encontrado: ${scannerElementId}`);
      }

      console.log('✅ Scanner element found:', scannerElement);

      // Crear instancia del escáner
      console.log('🔧 QrScanner: Creating Html5Qrcode instance with ID:', scannerElementId);
      const html5QrCode = new Html5Qrcode(scannerElementId);
      html5QrCodeRef.current = html5QrCode;

      // Configuración del escáner
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      // Priorizar cámara posterior (especialmente importante en iOS)
      let preferredCameraIndex = 0;

      // Buscar cámara posterior por etiqueta
      const backCameraIndex = devices.findIndex(device =>
        device.label.toLowerCase().includes('back') ||
        device.label.toLowerCase().includes('rear') ||
        device.label.toLowerCase().includes('trasera') ||
        device.label.toLowerCase().includes('posterior')
      );

      // Si encontramos una cámara posterior, usarla
      if (backCameraIndex !== -1) {
        preferredCameraIndex = backCameraIndex;
      } else if (devices.length > 1) {
        // En iOS, la cámara posterior suele ser la segunda (índice 1)
        preferredCameraIndex = 1;
      }

      setCurrentCameraIndex(preferredCameraIndex);
      const cameraId = devices[preferredCameraIndex].id;

      console.log('🎬 QrScanner: Starting scanner with camera:', cameraId);

      await html5QrCode.start(
        cameraId,
        config,
        (decodedText) => {
          console.log('✅ QrScanner: QR Code detected:', decodedText);
          handleScanSuccess(decodedText);
        },
        () => {
          // Silenciar errores de escaneo continuo
        }
      );

      if (isMountedRef.current) {
        setScanning(true);
        setLoading(false);
        console.log('✅ QrScanner: Html5Qrcode scanner started successfully');
      }

    } catch (err) {
      console.error('❌ QrScanner: Error initializing scanner:', err);
      setError(`Error al inicializar el escáner: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  // Función para detener el escáner
  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        const isScanning = html5QrCodeRef.current.getState();
        if (isScanning === 2) { // Html5QrcodeScannerState.SCANNING
          await html5QrCodeRef.current.stop();
          console.log('🛑 QrScanner: Html5Qrcode scanner stopped');
        }
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
      setScanning(false);
      setLoading(false);
    } catch (error) {
      console.error('❌ QrScanner: Error stopping scanner:', error);
    }
  };

  // Manejar éxito del escaneo
  // Función para cambiar de cámara
  const switchCamera = async () => {
    if (availableCameras.length <= 1) {
      setToastMessage('Solo hay una cámara disponible');
      setToastColor('danger');
      setShowToast(true);
      return;
    }

    try {
      setLoading(true);

      // Detener el escáner actual
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
      }

      // Cambiar al siguiente índice de cámara
      const nextCameraIndex = (currentCameraIndex + 1) % availableCameras.length;
      setCurrentCameraIndex(nextCameraIndex);

      // Reiniciar el escáner con la nueva cámara
      const html5QrCode = html5QrCodeRef.current;
      if (html5QrCode) {
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        };

        await html5QrCode.start(
          availableCameras[nextCameraIndex].id,
          config,
          (decodedText) => {
            console.log('✅ QrScanner: QR Code detected:', decodedText);
            handleScanSuccess(decodedText);
          },
          () => {
            // Silenciar errores de escaneo continuo
          }
        );

        setScanning(true);
        setLoading(false);

        setToastMessage(`Cambiado a ${availableCameras[nextCameraIndex].label || 'cámara ' + (nextCameraIndex + 1)}`);
        setToastColor('success');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error switching camera:', error);
      setError('Error al cambiar de cámara');
      setLoading(false);
    }
  };

  const handleScanSuccess = (decodedText: string) => {
    console.log('🎯 QrScanner: Scan successful:', decodedText);
    
    // Detener el escáner
    stopScanner();
    
    // Mostrar toast de éxito
    setToastMessage('¡QR escaneado exitosamente!');
    setToastColor('success');
    setShowToast(true);
    
    // Llamar al callback
    onScanSuccess(decodedText);
    
    // Cerrar el modal después de un breve delay
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  // Manejar cierre del modal
  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} className="qr-scanner-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            {/* Botón para cambiar cámara - solo mostrar si hay múltiples cámaras */}
            {availableCameras.length > 1 && scanning && (
              <IonButton onClick={switchCamera} disabled={loading}>
                <IonIcon icon={cameraReverse} />
              </IonButton>
            )}
            <IonButton onClick={handleClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="qr-scanner-content">
        {loading && (
          <div className="scanner-loading">
            <IonSpinner name="crescent" />
            <IonText>
              <p>Iniciando cámara...</p>
            </IonText>
          </div>
        )}

        {error && (
          <div className="scanner-error">
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
            <IonButton onClick={initializeScanner} fill="outline">
              Reintentar
            </IonButton>
          </div>
        )}

        {/* Elemento del escáner siempre presente cuando el modal está abierto */}
        <div className="scanner-container" style={{ display: error ? 'none' : 'block' }}>
          <div id={scannerElementId} className="qr-scanner"></div>
          {scanning && (
            <div className="scanner-overlay">
              <div className="scanner-frame"></div>
              <IonText color="light">
                <p>Apunta la cámara hacia el código QR</p>
              </IonText>
            </div>
          )}
        </div>
      </IonContent>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        position="bottom"
        color={toastColor}
      />
    </IonModal>
  );
};

export default QrScanner;
